// services/nativeAuthReturn.ts
import { Browser } from '@capacitor/browser';
import { enqueueSnackbar } from 'notistack';

import { getAuthUrl, type AuthClientError } from './authClient';
import { resumeSessionFromCookie } from './auth';
import { navigateForAuth } from './authNavigation';
import { attachStoredReferralCodeIfPresent } from './referrals';
import { reportGoogleAdsSignupConversion } from '../utils/googleAds';
import { capture } from '../utils/analytics';

/**
 * Native (Capacitor) sign-in handoff (ORA-987, backend ORA-986
 * `src/auth/plugins/nativeReturn.ts`).
 *
 * The OAuth provider runs in the system browser, whose cookies the app's
 * WebView never sees. The app therefore opens the flow's start in that
 * browser too (`GET /api/auth/native-start`, ORA-1002), so Better Auth's
 * OAuth `state` cookie is set where the callback lands. The backend builds
 * the callback URLs: the callback lands on its `native-return` route, which
 * hands the session to the app through the `oraapp://auth` deep link:
 *
 * - signed in: `?ott=<token>&isNewUser=true|false&ctx=<ctx>`; the app trades
 *   the token for its own session cookie
 *   (`POST /api/auth/native-return/verify`, not Better Auth's
 *   one-time-token plugin, which the backend does not run);
 * - 2FA pending: `?twoFactor=1&tfa=<token>&ctx=<ctx>`; the 2FA page sends the
 *   code with the token to `POST /api/auth/native-2fa/verify-totp` (or
 *   `verify-backup-code`). The token lasts 3 minutes and is single use;
 * - failed: `?error=<code>&ctx=<ctx>` (the flow's error callback is
 *   `native-return?error=1`); the app shows the auth page's error copy.
 *
 * The flow is PKCE-bound: the app sends `sha256(verifier)` as native-start's
 * `challenge` when it starts, and only the app that holds the verifier can
 * redeem either token, so a deep link intercepted by another app is useless.
 * The app also sends its flow id as `flow`; every deep link echoes it as
 * `flow`, and a link whose `flow` is not the sign-in in progress is ignored.
 *
 * The sign-in context `ctx` (base64url JSON the backend puts on the callback
 * URL) is applied by the backend itself when the token is redeemed
 * (`native-return/verify`, `native-2fa/verify-*`); the app no longer posts it
 * to `/auth/apply-signin-context`. A context the backend refuses (an
 * ambassador sign-up without a .edu email) still signs in; the response says
 * so in `signInContextError`, and the app shows the auth page's copy.
 *
 * Every backend name lives in this file so a rename is a one-line fix.
 */

/** Better Auth path the app opens in the system browser to start a native
 * sign-in. Query `provider`, `challenge`, `flow`, `ctx?`, `data?`; it
 * redirects to the provider. */
export const NATIVE_START_PATH = '/native-start';

/** Better Auth path that redeems the deep link's one-time token. Body
 * `{ token, verifier }` → `{ success, userId }`; sets the session cookie.
 * Better Auth's own one-time-token plugin is not used. */
export const NATIVE_RETURN_VERIFY_PATH = '/native-return/verify';

/** Better Auth paths that finish a native sign-in's second factor. Body
 * `{ tfa, code, trustDevice?, verifier }`. */
export const NATIVE_TWO_FACTOR_VERIFY_PATHS = {
  totp: '/native-2fa/verify-totp',
  backup: '/native-2fa/verify-backup-code',
} as const;

/** The deep link the backend opens: `oraapp://auth?...`. */
export const NATIVE_DEEP_LINK_PROTOCOL = 'oraapp:';
export const NATIVE_DEEP_LINK_HOST = 'auth';

/** Deep-link (and callback URL) query keys. */
export const NATIVE_DEEP_LINK_PARAMS = {
  oneTimeToken: 'ott',
  isNewUser: 'isNewUser',
  twoFactor: 'twoFactor',
  twoFactorToken: 'tfa',
  signInContext: 'ctx',
  error: 'error',
  flowId: 'flow',
} as const;

/** The error codes `native-return` passes through; anything else reads as
 * `oauth-failed`. The auth page has the copy (and analytics) for each. */
export const NATIVE_OAUTH_ERROR_CODES: ReadonlySet<string> = new Set([
  'account_not_linked',
  'ambassador-email-not-allowed',
  'oauth-failed',
  // native-start could not begin the flow (rate limit, provider failure).
  'start-failed',
  'two-factor-required',
]);
const DEFAULT_NATIVE_OAUTH_ERROR = 'oauth-failed';

/** The auth page's copy for `ambassador-email-not-allowed`. */
export const AMBASSADOR_EMAIL_NOT_ALLOWED_MESSAGE =
  'Ambassador signups require a .edu email.';
const AMBASSADOR_EMAIL_NOT_ALLOWED = 'ambassador-email-not-allowed';

/** The only keys `native-start` / `native-return` accept in `ctx` and `data`;
 * anything else is a 400. */
export const SIGN_IN_CONTEXT_KEYS: ReadonlySet<string> = new Set([
  'referralId',
  'buyIntent',
  'timezone',
  'cohort',
  'unlockEmail',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent',
  'landingPath',
  'ambassador',
  'inferred',
  'platform',
]);

/** The backend refuses a `ctx` or `data` over 2 KB (decoded). */
export const MAX_SIGN_IN_CONTEXT_BYTES = 2048;

/** Holds the native 2FA token between the deep link and the 2FA page. */
export const NATIVE_TWO_FACTOR_TOKEN_KEY = 'ora-native-tfa';
/** Written by older builds (the ctx of a pending 2FA sign-in); only cleared
 * now that the backend applies the context. */
export const NATIVE_TWO_FACTOR_CONTEXT_KEY = 'ora-native-tfa-ctx';

/** The PKCE verifier of the native flow in progress. */
export const NATIVE_PKCE_STORAGE_KEY = 'ora-native-pkce';

/** Longer than any provider round trip; an older verifier is discarded. */
const NATIVE_PKCE_MAX_AGE_MS = 30 * 60 * 1000;

export type SignInContextValue = string | boolean;

const bytesToBase64Url = (bytes: Uint8Array): string => {
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const toBase64Url = (text: string): string =>
  bytesToBase64Url(new TextEncoder().encode(text));

const fromBase64Url = (value: string): string => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  return new TextDecoder().decode(
    Uint8Array.from(binary, (char) => char.charCodeAt(0))
  );
};

/** The attribution fields, dropped first when a context is over 2 KB. */
const ATTRIBUTION_KEYS: ReadonlySet<string> = new Set([
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent',
  'landingPath',
  'inferred',
]);

const encodeIfWithinLimit = (
  entries: Array<[string, unknown]>
): string | undefined => {
  if (entries.length === 0) return undefined;
  const json = JSON.stringify(Object.fromEntries(entries));
  return new TextEncoder().encode(json).length > MAX_SIGN_IN_CONTEXT_BYTES
    ? undefined
    : toBase64Url(json);
};

/** Base64url JSON of the accepted keys whose values pass `isAccepted`.
 * Over the 2 KB limit, the attribution fields are dropped so the rest
 * (cohort, buy intent, platform, ambassador, unlock email) still travels;
 * undefined when nothing passes or even that is over the limit. */
const encodeContextParam = (
  context: Record<string, unknown>,
  isAccepted: (value: unknown) => boolean
): string | undefined => {
  const accepted = Object.entries(context).filter(
    ([key, value]) => SIGN_IN_CONTEXT_KEYS.has(key) && isAccepted(value)
  );

  return (
    encodeIfWithinLimit(accepted) ??
    encodeIfWithinLimit(accepted.filter(([key]) => !ATTRIBUTION_KEYS.has(key)))
  );
};

const isContextValue = (value: unknown): boolean =>
  (typeof value === 'string' && value !== '') || typeof value === 'boolean';

/**
 * The sign-in context as the `ctx` parameter: only the accepted keys, only
 * string and boolean values, within the backend's 2 KB limit (see
 * `encodeContextParam`). Undefined when there is nothing to carry.
 */
export const encodeSignInContext = (
  context: Record<string, unknown>
): string | undefined => encodeContextParam(context, isContextValue);

/**
 * The sign-up context as native-start's `data` parameter, which the backend
 * puts in the OAuth state's `additionalData` for the sign-up hooks: the
 * accepted keys, with string, boolean or number values, within the 2 KB
 * limit (see `encodeContextParam`). Undefined when there is nothing to carry.
 */
export const encodeNativeStartData = (
  context: Record<string, unknown>
): string | undefined =>
  encodeContextParam(
    context,
    (value) =>
      isContextValue(value) ||
      (typeof value === 'number' && Number.isFinite(value))
  );

/** Reads a `ctx` parameter back; null for anything malformed. */
export const decodeSignInContext = (
  ctx: string | null | undefined
): Record<string, SignInContextValue> | null => {
  if (!ctx) return null;
  try {
    const parsed = JSON.parse(fromBase64Url(ctx)) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    const entries = Object.entries(parsed).filter(
      ([key, value]) =>
        SIGN_IN_CONTEXT_KEYS.has(key) &&
        (typeof value === 'string' || typeof value === 'boolean')
    ) as Array<[string, SignInContextValue]>;

    return entries.length > 0 ? Object.fromEntries(entries) : null;
  } catch {
    return null;
  }
};

interface NativePkceFlow {
  id: string;
  verifier: string;
  createdAt: number;
  /** The auth page the sign-in started from: errors are shown there. */
  authPath?: string;
}

const DEFAULT_AUTH_PATH = '/auth';
const AUTH_PATHS: ReadonlySet<string> = new Set(['/auth', '/faculty/auth']);
// Memory first; localStorage covers the OS killing or recreating the
// WebView while the system browser is open (sessionStorage would not).
// The 30-minute expiry bounds how long a verifier lingers.
let pendingPkceFlow: NativePkceFlow | null = null;

/**
 * Starts a native flow: a fresh 32-byte verifier, remembered until the deep
 * link comes back. Resolves to the challenge for `additionalData`
 * (`base64url(sha256(verifier))`, the verifier being its base64url string)
 * and the flow id the deep link must echo as `flow`.
 * A new flow replaces any earlier one: only one sign-in runs at a time.
 * `authPath` is the auth page a failed sign-in returns to.
 */
export const startNativePkceFlow = async (
  authPath: string = DEFAULT_AUTH_PATH
): Promise<{ challenge: string; flowId: string }> => {
  const verifier = bytesToBase64Url(crypto.getRandomValues(new Uint8Array(32)));

  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  );

  pendingPkceFlow = {
    id: bytesToBase64Url(crypto.getRandomValues(new Uint8Array(12))),
    verifier,
    createdAt: Date.now(),
    authPath,
  };

  try {
    localStorage.setItem(
      NATIVE_PKCE_STORAGE_KEY,
      JSON.stringify(pendingPkceFlow)
    );
  } catch {
    // Memory alone still covers the usual case.
  }

  return {
    challenge: bytesToBase64Url(new Uint8Array(digest)),
    flowId: pendingPkceFlow.id,
  };
};

/** The flow in progress, or null when there is none (or it expired). */
const readNativePkceFlow = (): NativePkceFlow | null => {
  let flow = pendingPkceFlow;
  if (!flow) {
    try {
      flow = JSON.parse(
        localStorage.getItem(NATIVE_PKCE_STORAGE_KEY) ?? 'null'
      ) as NativePkceFlow | null;
    } catch {
      flow = null;
    }
  }

  if (
    !flow ||
    typeof flow.verifier !== 'string' ||
    typeof flow.createdAt !== 'number' ||
    Date.now() - flow.createdAt > NATIVE_PKCE_MAX_AGE_MS
  ) {
    return null;
  }
  return flow;
};

/** The verifier of the flow in progress, or null when there is none. */
export const readNativeVerifier = (): string | null =>
  readNativePkceFlow()?.verifier ?? null;

/** The auth page the flow in progress started from (default `/auth`). */
const readNativeAuthPath = (): string => {
  const authPath = readNativePkceFlow()?.authPath;
  return authPath && AUTH_PATHS.has(authPath) ? authPath : DEFAULT_AUTH_PATH;
};

/** Forgets the verifier: it is single use, like the tokens it unlocks. */
export const clearNativePkceFlow = (): void => {
  pendingPkceFlow = null;
  try {
    localStorage.removeItem(NATIVE_PKCE_STORAGE_KEY);
  } catch {
    // Nothing to remove.
  }
};

interface NativeVerifyResult {
  success: boolean;
  error?: AuthClientError;
  /** The stored sign-in context the backend refused; still signed in. */
  signInContextError?: string;
}

/** Posts to a Better Auth route; the error half mirrors the client's. */
const postAuth = async (
  path: string,
  body: Record<string, unknown>
): Promise<NativeVerifyResult> => {
  let response: Response;
  try {
    response = await fetch(getAuthUrl(path), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    return { success: false, error: { message: 'Network error' } };
  }

  const payload = (await response.json().catch(() => null)) as {
    code?: unknown;
    message?: unknown;
    signInContextError?: unknown;
  } | null;

  if (response.ok) {
    return typeof payload?.signInContextError === 'string'
      ? { success: true, signInContextError: payload.signInContextError }
      : { success: true };
  }

  return {
    success: false,
    error: {
      status: response.status,
      code: typeof payload?.code === 'string' ? payload.code : undefined,
      message:
        typeof payload?.message === 'string' ? payload.message : undefined,
    },
  };
};

/**
 * The URL that starts a native sign-in in the system browser: the backend
 * builds the native-return callbacks from `ctx` and puts `data`, the
 * challenge and the flow id in the OAuth state.
 */
export const buildNativeStartUrl = ({
  provider,
  challenge,
  flowId,
  context = {},
}: {
  provider: string;
  challenge: string;
  flowId: string;
  context?: Record<string, unknown>;
}): string => {
  const params = new URLSearchParams({ provider, challenge, flow: flowId });
  const ctx = encodeSignInContext(context);
  if (ctx) params.set(NATIVE_DEEP_LINK_PARAMS.signInContext, ctx);
  const data = encodeNativeStartData(context);
  if (data) params.set('data', data);
  return getAuthUrl(`${NATIVE_START_PATH}?${params.toString()}`);
};

/** `flowId` is the link's `flow` (null when the backend sent none);
 * `signInContext` is display-only: the backend applies it. */
export type NativeAuthDeepLink =
  | {
      kind: 'session';
      oneTimeToken: string;
      isNewUser: boolean;
      signInContext: string | null;
      flowId: string | null;
    }
  | {
      kind: 'twoFactor';
      twoFactorToken: string;
      signInContext: string | null;
      flowId: string | null;
    }
  | {
      kind: 'error';
      /** One of `NATIVE_OAUTH_ERROR_CODES`. */
      error: string;
      flowId: string | null;
    };

/** The `oraapp://auth` deep link's query, or null for any other URL. */
const readNativeAuthDeepLinkParams = (
  rawUrl: string
): URLSearchParams | null => {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  if (
    url.protocol !== NATIVE_DEEP_LINK_PROTOCOL ||
    url.hostname !== NATIVE_DEEP_LINK_HOST ||
    !['', '/'].includes(url.pathname)
  ) {
    return null;
  }
  return url.searchParams;
};

/** Reads an `oraapp://auth?...` deep link. Null for any other URL and for
 * an auth link without `ott`, `tfa` or `error`. */
export const parseNativeAuthDeepLink = (
  rawUrl: string
): NativeAuthDeepLink | null => {
  const params = readNativeAuthDeepLinkParams(rawUrl);
  if (!params) return null;

  const signInContext = params.get(NATIVE_DEEP_LINK_PARAMS.signInContext);
  const flowId = params.get(NATIVE_DEEP_LINK_PARAMS.flowId);
  const oneTimeToken = params.get(NATIVE_DEEP_LINK_PARAMS.oneTimeToken);
  if (oneTimeToken) {
    const isNewUser = params.get(NATIVE_DEEP_LINK_PARAMS.isNewUser);
    return {
      kind: 'session',
      oneTimeToken,
      isNewUser: isNewUser === 'true' || isNewUser === '1',
      signInContext,
      flowId,
    };
  }

  const twoFactorToken = params.get(NATIVE_DEEP_LINK_PARAMS.twoFactorToken);
  if (params.get(NATIVE_DEEP_LINK_PARAMS.twoFactor) === '1' && twoFactorToken) {
    return { kind: 'twoFactor', twoFactorToken, signInContext, flowId };
  }

  const error = params.get(NATIVE_DEEP_LINK_PARAMS.error);
  if (error !== null) {
    return {
      kind: 'error',
      error: NATIVE_OAUTH_ERROR_CODES.has(error)
        ? error
        : DEFAULT_NATIVE_OAUTH_ERROR,
      flowId,
    };
  }

  return null;
};

/**
 * Trades the deep link's one-time token (with this app's PKCE verifier) for
 * the WebView's session cookie. The verifier is spent either way: the token
 * is single use. Refusals: 403 `NO_NATIVE_SIGNIN`, 403
 * `NATIVE_SIGNIN_EXPIRED` (older than 5 minutes), 401 `INVALID_NATIVE_TOKEN`
 * (wrong verifier, replay or expiry); each means start the sign-in again.
 */
export const verifyNativeOneTimeToken = async (
  token: string
): Promise<NativeVerifyResult> => {
  const verifier = readNativeVerifier();
  if (!verifier) {
    return {
      success: false,
      error: { code: 'NO_NATIVE_SIGNIN', status: 403 },
    };
  }

  const result = await postAuth(NATIVE_RETURN_VERIFY_PATH, {
    token,
    verifier,
  });

  clearNativePkceFlow();
  return result;
};

const readSessionItem = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const takeSessionItem = (key: string): string | null => {
  const value = readSessionItem(key);
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to remove.
  }
  return value;
};

/** True while a native sign-in waits for its second factor. */
export const hasPendingNativeTwoFactor = (): boolean =>
  Boolean(readSessionItem(NATIVE_TWO_FACTOR_TOKEN_KEY));

export const clearPendingNativeTwoFactor = (): void => {
  takeSessionItem(NATIVE_TWO_FACTOR_TOKEN_KEY);
  takeSessionItem(NATIVE_TWO_FACTOR_CONTEXT_KEY);
  clearNativePkceFlow();
};

/**
 * A sign-in whose stored context the backend refused: the same copy and
 * event as the auth page's failed ambassador sign-up, but the student stays
 * signed in.
 */
const reportSignInContextError = (signInContextError?: string): void => {
  if (signInContextError !== AMBASSADOR_EMAIL_NOT_ALLOWED) return;

  enqueueSnackbar(AMBASSADOR_EMAIL_NOT_ALLOWED_MESSAGE, { variant: 'warning' });

  capture('signup_error', {
    method: localStorage.getItem('last-used-auth') || 'unknown',
    errorType: 'ambassador_email_not_allowed',
  });
};

/** Error codes after which the pending native sign-in cannot continue. */
const SPENT_TWO_FACTOR_CODES = new Set([
  'INVALID_TWO_FACTOR_TOKEN',
  'INVALID_TWO_FACTOR_COOKIE',
  'TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE',
  'NO_NATIVE_SIGNIN',
  'NATIVE_SIGNIN_EXPIRED',
  'INVALID_NATIVE_TOKEN',
]);

/**
 * Finishes a native sign-in's second factor: posts the code with the deep
 * link's token (the backend applies the stored sign-in context), then signs
 * the store in.
 */
export const verifyNativeTwoFactor = async ({
  code,
  method,
  trustDevice,
}: {
  code: string;
  method: 'totp' | 'backup';
  trustDevice: boolean;
}): Promise<{ success: boolean; error?: AuthClientError }> => {
  const twoFactorToken = readSessionItem(NATIVE_TWO_FACTOR_TOKEN_KEY);
  const verifier = readNativeVerifier();
  if (!twoFactorToken || !verifier) {
    clearPendingNativeTwoFactor();
    return {
      success: false,
      error: { code: 'INVALID_TWO_FACTOR_TOKEN', status: 401 },
    };
  }

  const result = await postAuth(NATIVE_TWO_FACTOR_VERIFY_PATHS[method], {
    tfa: twoFactorToken,
    code,
    trustDevice,
    verifier,
  });

  if (!result.success) {
    const errorCode = result.error?.code;
    if (errorCode && SPENT_TWO_FACTOR_CODES.has(errorCode)) {
      clearPendingNativeTwoFactor();
    }
    return result;
  }

  clearPendingNativeTwoFactor();

  const userId = await resumeSessionFromCookie();
  if (!userId) {
    return {
      success: false,
      error: { code: 'INVALID_TWO_FACTOR_TOKEN', status: 401 },
    };
  }

  reportSignInContextError(result.signInContextError);
  void attachStoredReferralCodeIfPresent();
  return { success: true };
};

const closeSystemBrowser = () => Browser.close().catch(() => undefined);

/** The auth page's failed-sign-in address: it shows the copy for `error`
 * (and reports it) exactly as after a failed web redirect. */
const failedSignInPath = (authPath: string, error: string): string =>
  `${authPath}?oauth=failed&error=${encodeURIComponent(error)}`;

/**
 * Handles one `appUrlOpen` URL. Resolves true when it was an auth deep link
 * (handled, failed or ignored), false when the URL belongs to someone else.
 *
 * Ignored, with the pending flow left as it was: an auth link without `ott`,
 * `tfa` or `error`, and one whose `flow` is not the sign-in in progress (a
 * stale link, or one another app opened). While a flow is pending, a sign-in
 * or 2FA link without `flow` is ignored too; an error link without one is
 * still shown.
 *
 * A completed sign-in only updates the store: whichever page started it
 * (the auth page, /unlock, the webinar modal) already reacts to
 * `isAuthenticated`. A failed one goes to the auth page it started from,
 * with the same error copy as on the web. (Accounts are linked on the web
 * only.)
 */
export const handleNativeAuthUrl = async (rawUrl: string): Promise<boolean> => {
  if (!readNativeAuthDeepLinkParams(rawUrl)) return false;

  const link = parseNativeAuthDeepLink(rawUrl);
  if (!link) {
    console.warn('Ignored an oraapp://auth link without a sign-in result');
    return true;
  }

  // A sign-in or 2FA link must name the pending flow; an error link may
  // omit `flow` (the backend cannot always echo it), but a present `flow`
  // must still match.
  const pendingFlowId = readNativePkceFlow()?.id ?? null;

  const isFlowMatch =
    link.flowId === null
      ? link.kind === 'error' || pendingFlowId === null
      : link.flowId === pendingFlowId;

  if (!isFlowMatch) {
    console.warn('Ignored an oraapp://auth link for another sign-in flow');
    return true;
  }

  void closeSystemBrowser();

  if (link.kind === 'twoFactor') {
    try {
      sessionStorage.setItem(NATIVE_TWO_FACTOR_TOKEN_KEY, link.twoFactorToken);
      sessionStorage.removeItem(NATIVE_TWO_FACTOR_CONTEXT_KEY);
    } catch {
      // Without storage the 2FA page cannot finish; it says so.
    }
    // The 2FA page returns to the sign-in page the flow started from, so a
    // faculty sign-in lands on /faculty, not / (ORA-1004).
    const fromFaculty = readNativeAuthPath() === '/faculty/auth';

    navigateForAuth(
      `/auth/2fa?mode=native${fromFaculty ? '&from=faculty' : ''}`
    );

    return true;
  }

  const authPath = readNativeAuthPath();

  if (link.kind === 'error') {
    clearNativePkceFlow();
    navigateForAuth(failedSignInPath(authPath, link.error));
    return true;
  }

  const verified = await verifyNativeOneTimeToken(link.oneTimeToken);
  const userId = verified.success ? await resumeSessionFromCookie() : null;
  if (!userId) {
    navigateForAuth(failedSignInPath(authPath, 'session_expired'));
    return true;
  }

  reportSignInContextError(verified.signInContextError);
  if (link.isNewUser) void reportGoogleAdsSignupConversion();
  void attachStoredReferralCodeIfPresent();
  return true;
};
